// Service imports:
import { getDatePair, turnDateToSimpleTime } from '../../services/utils';
import { bugsnagNotify, fetchResponseToDetailedString } from '../../services/bugsnagUtils';
import { GET_SIGTI_API_URL } from '../../config';
// Query imports:
import { requestAlertsSummaryPerClientIdQuery, requestAlertsSummaryPerTraderIdQuery, requestAlertsSummaryQuery, requestBrokenTradeReportsQuery, requestCancelledTradesQuery } from '../queries/reportQueries';
// Type imports:
import { AppDispatch } from '../../index'; // Use the root dispatch type from your store
import { IPrivateTrade, IPrivateInetTradeReport, IPrivateGeniumInetTradeReport } from '../../types/api/PrivateTypes';
import { ReportActions, REQUEST_CANCELLED_TRADES_WAITING, REQUEST_CANCELLED_TRADES_SUCCESS, REQUEST_CANCELLED_TRADES_FAILURE, REQUEST_BROKEN_TRADE_REPORTS_WAITING, REQUEST_BROKEN_TRADE_REPORTS_SUCCESS, REQUEST_BROKEN_TRADE_REPORTS_FAILURE, REQUEST_ALERTS_SUMMARY_WAITING, REQUEST_ALERTS_SUMMARY_SUCCESS, REQUEST_ALERTS_SUMMARY_FAILURE, REQUEST_ALERTS_SUMMARY_PER_TRADER_ID_WAITING, REQUEST_ALERTS_SUMMARY_PER_TRADER_ID_SUCCESS, REQUEST_ALERTS_SUMMARY_PER_TRADER_ID_FAILURE, REQUEST_ALERTS_SUMMARY_PER_CLIENT_ID_WAITING, REQUEST_ALERTS_SUMMARY_PER_CLIENT_ID_SUCCESS, REQUEST_ALERTS_SUMMARY_PER_CLIENT_ID_FAILURE} from '../../types/redux/ReportTypes';
import { IAlertsSummary, IAlertsSummaryPerClientId, IAlertsSummaryPerTraderId } from '../../types/api/ReportTypes';
import { REQUEST_FORBIDDEN } from '../../types/redux/LoginTypes';

// ================================ CHECK IF FORBIDDEN RESOURCE (LOGOUT IF TRUE) ================================
const checkIfForbiddenResource = (responseBody: any) => {
    if (responseBody.errors && responseBody.errors[0].message === "Forbidden resource") {
        return true;
    }
    return false;
}

const requestForbidden = () => ({
    type: REQUEST_FORBIDDEN,
});

// =================================== REQUEST CANCELLED TRADES EMS ===================================
const requestCancelledTradesThunk = async (dispatch: AppDispatch, fromDate: string, toDate: string, accessToken: string) => {
    // Format time string.
    const simpleFromDate = turnDateToSimpleTime(new Date(fromDate));
    const simpleToDate = turnDateToSimpleTime(new Date(toDate));
    const datePair = getDatePair(simpleFromDate, simpleToDate);

    // Set as waiting for data.
    dispatch(requestCancelledTradesWaiting(datePair));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestCancelledTradesQuery();

    const queryObject = {
        query: query,
        variables: {
            fromDate: new Date(simpleFromDate),
            toDate: new Date(simpleToDate)
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            if (!responseBody.data) {
                dispatch(requestCancelledTradesFailure(datePair, 'Session Expired'));
                return;
            }
            const trades = responseBody.data.privateCancelledTrades;
            dispatch(requestCancelledTradesSuccess(datePair, trades));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Cancelled Trades', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestCancelledTradesFailure(datePair, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestCancelledTradesFailure(datePair, 'Network Error'));
    }
}

const requestCancelledTradesWaiting = (datePair: string): ReportActions => {
    return {
        type: REQUEST_CANCELLED_TRADES_WAITING,
        payload: {
            datePair
        }
    };
};

const requestCancelledTradesSuccess = (datePair: string, cancelledTrades: IPrivateTrade[]): ReportActions => {
    return {
        type: REQUEST_CANCELLED_TRADES_SUCCESS,
        payload: {
            datePair,
            cancelledTrades
        }
    };
};

const requestCancelledTradesFailure = (datePair: string, error: string): ReportActions => {
    return {
        type: REQUEST_CANCELLED_TRADES_FAILURE,
        payload: {
            datePair,
            error
        }
    };
};

export const requestCancelledTrades =
    (fromDate: string, toDate: string, accessToken: string) =>
    (dispatch: AppDispatch) =>
    requestCancelledTradesThunk(dispatch, fromDate, toDate, accessToken);


// =================================== REQUEST BROKEN TRADE REPORTS EMS ===================================
const requestBrokenTradeReportsThunk = async (dispatch: AppDispatch, fromDate: string, toDate: string, accessToken: string) => {
    // Format time string.
    const simpleFromDate = turnDateToSimpleTime(new Date(fromDate));
    const simpleToDate = turnDateToSimpleTime(new Date(toDate));
    const datePair = getDatePair(simpleFromDate, simpleToDate);
    // Set as waiting for data.
    dispatch(requestBrokenTradeReportsWaiting(datePair));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestBrokenTradeReportsQuery();

    const queryObject = {
        query: query,
        variables: {
            fromDate: new Date(simpleFromDate),
            toDate: new Date(simpleToDate)
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });

        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            if (!responseBody.data?.privateBrokenTradeReports) {
                dispatch(requestBrokenTradeReportsFailure(datePair, 'Session Expired'));
                return;
            }
            const { geniuminetTradeReports, inetTradeReports } = responseBody.data.privateBrokenTradeReports;

            dispatch(requestBrokenTradeReportsSuccess(datePair, inetTradeReports, geniuminetTradeReports));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Broken Trade Reports', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestBrokenTradeReportsFailure(datePair, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestBrokenTradeReportsFailure(datePair, 'Network Error'));
    }
}

const requestBrokenTradeReportsWaiting = (datePair: string): ReportActions => {
    return {
        type: REQUEST_BROKEN_TRADE_REPORTS_WAITING,
        payload: {
            datePair
        }
    };
};

const requestBrokenTradeReportsSuccess = (
    datePair: string,
    brokenInetTradeReports: IPrivateInetTradeReport[],
    brokenGeniumTradeReports: IPrivateGeniumInetTradeReport[]
): ReportActions => {
    return {
        type: REQUEST_BROKEN_TRADE_REPORTS_SUCCESS,
        payload: {
            datePair,
            brokenInetTradeReports,
            brokenGeniumTradeReports
        }
    };
};

const requestBrokenTradeReportsFailure = (datePair: string, error: string): ReportActions => {
    return {
        type: REQUEST_BROKEN_TRADE_REPORTS_FAILURE,
        payload: {
            datePair,
            error
        }
    };
};

export const requestBrokenTradeReports =
    (fromDate: string, toDate: string, accessToken: string) =>
    (dispatch: AppDispatch) =>
    requestBrokenTradeReportsThunk(dispatch, fromDate, toDate, accessToken);

    
// =================================== REQUEST ALERTS SUMMARY ===================================
const requestAlertsSummaryThunk = async (dispatch: AppDispatch, accessToken: string, fromDate: string, toDate: string) => {
    // Format time string.
    const simpleFromDate = turnDateToSimpleTime(new Date(fromDate));
    const simpleToDate = turnDateToSimpleTime(new Date(toDate));
    const datePair = getDatePair(simpleFromDate, simpleToDate);

    // Set waiting.
    dispatch(requestAlertsSummaryWaiting(datePair));

    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestAlertsSummaryQuery();

    const queryObject = {
        query,
        variables: {
            fromDate: new Date(fromDate),
            toDate: new Date(toDate)
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { alertsSummary } = responseBody.data;
            dispatch(requestAlertsSummarySuccess(datePair, alertsSummary));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Alerts', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestAlertsSummaryFailure(datePair, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestAlertsSummaryFailure(datePair, 'Network Error'));
    }
}

const requestAlertsSummaryWaiting = (datePair: string): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_WAITING,
        payload: {
            datePair
        }
    };
};

const requestAlertsSummarySuccess = (datePair: string, alertsSummary: Array<IAlertsSummary>): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_SUCCESS,
        payload: {
            datePair,
            alertsSummary
        }
    };
};

const requestAlertsSummaryFailure = (datePair: string, error: string): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_FAILURE,
        payload: {
            datePair,
            error
        }
    };
};

export const requestAlertsSummary =
    (accessToken: string, fromDate: string, toDate: string) => 
    (dispatch: AppDispatch) =>
    requestAlertsSummaryThunk(dispatch, accessToken, fromDate, toDate);



// =================================== REQUEST ALERTS PER TRADERID SUMMARY ===================================
const requestAlertsSummaryPerTraderIdThunk = async (dispatch: AppDispatch, accessToken: string, fromDate: string, toDate: string) => {
    // Format time string.
    const simpleFromDate = turnDateToSimpleTime(new Date(fromDate));
    const simpleToDate = turnDateToSimpleTime(new Date(toDate));
    const datePair = getDatePair(simpleFromDate, simpleToDate);
    // Set waiting.
    dispatch(requestAlertsSummaryPerTraderIdWaiting(datePair));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestAlertsSummaryPerTraderIdQuery();

    const queryObject = {
        query,
        variables: {
            fromDate: new Date(fromDate),
            toDate: new Date(toDate)
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { alertsSummaryByTraderId } = responseBody.data;
            dispatch(requestAlertsSummaryPerTraderIdSuccess(datePair, alertsSummaryByTraderId));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Alerts', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestAlertsSummaryPerTraderIdFailure(datePair, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestAlertsSummaryPerTraderIdFailure(datePair, 'Network Error'));
    }
}

const requestAlertsSummaryPerTraderIdWaiting = (datePair: string): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_PER_TRADER_ID_WAITING,
        payload: {
            datePair
        }
    };
};

const requestAlertsSummaryPerTraderIdSuccess = (datePair: string, alertsSummaryPerTraderId: Array<IAlertsSummaryPerTraderId>): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_PER_TRADER_ID_SUCCESS,
        payload: {
            datePair,
            alertsSummaryPerTraderId
        }
    };
};

const requestAlertsSummaryPerTraderIdFailure = (datePair: string, error: string): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_PER_TRADER_ID_FAILURE,
        payload: {
            datePair,
            error
        }
    };
};

export const requestAlertsSummaryPerTraderId =
    (accessToken: string, fromDate: string, toDate: string) => 
    (dispatch: AppDispatch) =>
    requestAlertsSummaryPerTraderIdThunk(dispatch, accessToken, fromDate, toDate);


// =================================== REQUEST ALERTS PER TRADERID SUMMARY ===================================
const requestAlertsSummaryPerClientIdThunk = async (dispatch: AppDispatch, accessToken: string, fromDate: string, toDate: string) => {
    // Format time string.
    const simpleFromDate = turnDateToSimpleTime(new Date(fromDate));
    const simpleToDate = turnDateToSimpleTime(new Date(toDate));
    const datePair = getDatePair(simpleFromDate, simpleToDate);
    // Set waiting.
    dispatch(requestAlertsSummaryPerClientIdWaiting(datePair));
    // Start request.
    const url = GET_SIGTI_API_URL();
    const query = requestAlertsSummaryPerClientIdQuery();

    const queryObject = {
        query,
        variables: {
            fromDate: new Date(fromDate),
            toDate: new Date(toDate)
        }
    };

    try {
        const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify(queryObject)
        });
        if (response.ok) {
            const responseBody = await response.json();
            // logOut if forbidden resource.
            if (checkIfForbiddenResource(responseBody)) {
                dispatch(requestForbidden());
            }
            const { alertsSummaryByClientId } = responseBody.data;
            dispatch(requestAlertsSummaryPerClientIdSuccess(datePair, alertsSummaryByClientId));
        } else {
            const { errorString } = await fetchResponseToDetailedString('Request Alerts', response);
            bugsnagNotify(new Error(errorString));
            dispatch(requestAlertsSummaryPerClientIdFailure(datePair, 'Request Failed'));
        }
    } catch (e) {
        bugsnagNotify(e as Error);
        dispatch(requestAlertsSummaryPerClientIdFailure(datePair, 'Network Error'));
    }
}

const requestAlertsSummaryPerClientIdWaiting = (datePair: string): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_PER_CLIENT_ID_WAITING,
        payload: {
            datePair
        }
    };
};

const requestAlertsSummaryPerClientIdSuccess = (datePair: string, alertsSummaryPerClientId: Array<IAlertsSummaryPerClientId>): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_PER_CLIENT_ID_SUCCESS,
        payload: {
            datePair,
            alertsSummaryPerClientId
        }
    };
};

const requestAlertsSummaryPerClientIdFailure = (datePair: string, error: string): ReportActions => {
    return {
        type: REQUEST_ALERTS_SUMMARY_PER_CLIENT_ID_FAILURE,
        payload: {
            datePair,
            error
        }
    };
};

export const requestAlertsSummaryPerClientId =
    (accessToken: string, fromDate: string, toDate: string) =>
    (dispatch: AppDispatch) =>
        requestAlertsSummaryPerClientIdThunk(dispatch, accessToken, fromDate, toDate);